import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Formats a date in YYYYMMDD format
 * @param date a Date object
 * @returns the date in YYYYMMDD format
 */
export const getApiDate = (date: Date) => dayjs(date).format('YYYYMMDD');

/**
 * Formats a date in YYYY-MM-DD format
 * @param date a Date object
 * @returns the date in YYYY-MM-DD format
 */
export const getInputDate = (date: Date) => dayjs(date).format('YYYY-MM-DD');

/**
 * Converts a date from YYYYMMDD to YYYY-MM-DD format
 * @param apiDate a timezone-less date in YYYYMMDD format
 * @returns the date in YYYY-MM-DD format
 */
export const getInputDateFromApi = (apiDate: string) =>
  dayjs(apiDate).format('YYYY-MM-DD');
/**
 * Converts a date from YYYYMMDD to YYYY.MM.DD format
 * @param apiDate a timezone-less date in YYYYMMDD format
 * @returns the date in YYYY.MM.DD format
 */
export const getInputDateFromApiDot = (apiDate: string) =>
  dayjs(apiDate).format('YYYY.MM.DD');

/**
 * Converts a date from YYYY-MM-DD to YYYYMMDD format
 * @param apiDate a timezone-less date in YYYY-MM-DD format
 * @returns the date in YYYYMMDD format
 */
export const getApiDateFromInput = (inputDate: string) =>
  dayjs(inputDate).format('YYYYMMDD');

/**
 * Parses a YYYYMMDD date to a Date object
 * @param apiDate a timezone-less date in YYYYMMDD format
 * @returns a Date object in the local timezone
 */
export const parseApiDate = (dateString: string) =>
  dayjs(dateString, 'YYYYMMDD').toDate();

/**
 * Outputs a timestamp in the format YYYYMMDD-HHmm or YYYYMMDD-HHmmss
 * @param date the date to be rendered
 * @param withSeconds whether to add the seconds to the timestamp
 * @returns the rendered timestamp
 */
export const timestamp = (date: Date, withSeconds: boolean = false) =>
  dayjs(date).format(`YYYYMMDD-HHmm${withSeconds ? 'ss' : ''}`);

/**
 * Returns the store's current date as a Date object at local midnight.
 *
 * When a time zone is provided (from the store metadata's `addressTimeZone`),
 * the calendar date is resolved in that time zone; otherwise the browser's
 * local date is used as a fallback. An unknown or malformed time zone (e.g.
 * bad data from CBD) also falls back to the browser's local date instead of
 * throwing.
 *
 * @param timeZone an IANA time zone, e.g. "Europe/Amsterdam"
 * @returns a Date at local midnight of the store's current calendar date
 */
export const getStoreToday = (timeZone?: string | null): Date => {
  let today: string;
  try {
    today = timeZone
      ? dayjs().tz(timeZone).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD');
  } catch {
    // Unknown or malformed time zone — fall back to the browser's local date.
    today = dayjs().format('YYYY-MM-DD');
  }
  return dayjs(today).toDate();
};
